import { lazy, Suspense, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useLocale, useTranslation } from "@ncp/i18n";
import { START_USE_CASE, type StartUseCase } from "@ncp/page-host";
import type { SpaceUseCase } from "@ncp/assistant-sdk";
import { toast } from "@ncp/ui";
import { spaceText } from "../app/nav";
import { useNewChat } from "../app/useNewChat";
import { useWorkspace } from "../app/workspace-context";
// The start dialog brings the form engine (ajv, react-hook-form): it loads when a use case asks for it, never with the first paint.
const OpenMatterDialog = lazy(() => import("../features/matters/OpenMatterDialog").then((m) => ({ default: m.OpenMatterDialog })));

/**
 * Answers a page's `ncp.ui.open({ useCase })` and the shell's own starts (the New menu, home, the palette): the space's start dialog
 * with the page's parameters over the use case's own, the use case's page, or a chat with its prompt.
 */
export function UseCaseStarter() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const newChat = useNewChat();
  const [start, setStart] = useState<{ useCase: SpaceUseCase; request: StartUseCase } | null>(null);
  const useCases = workspace?.space?.useCases;

  useEffect(() => {
    const onStart = (e: Event) => {
      const request = (e as CustomEvent<StartUseCase>).detail;
      const useCase = useCases?.find((u) => u.key === request.useCase);
      if (!useCase) {
        toast.error(t("assistant:hub.home.useCaseMissing", { key: request.useCase }));
        return;
      }
      if (useCase.playbook) setStart({ useCase, request });
      else if (useCase.page) {
        // The page reads which use case opened it, and its parameters, from its URL (useParam).
        const query = new URLSearchParams({ useCase: useCase.key });
        for (const [k, v] of Object.entries(useCase.parameters ?? {})) if (v != null) query.set(k, typeof v === "string" ? v : JSON.stringify(v));
        for (const [k, v] of Object.entries(request.params)) query.set(k, v);
        navigate(`/p/${encodeURIComponent(useCase.page)}?${query}`);
      } else if (useCase.prompt) newChat(undefined, useCase.prompt);
    };
    window.addEventListener(START_USE_CASE, onStart);
    return () => window.removeEventListener(START_USE_CASE, onStart);
  }, [useCases, navigate, t, newChat]);

  if (!start?.useCase.playbook) return null;
  const { useCase, request } = start;
  return (
    <Suspense fallback={null}>
    <OpenMatterDialog
      key={`${useCase.key}:${JSON.stringify(request.params)}`}
      open
      onOpenChange={(o) => !o && setStart(null)}
      playbookKey={useCase.playbook}
      initial={{
        title: request.title ?? spaceText(useCase.title, locale),
        summary: request.summary ?? spaceText(useCase.description, locale),
        params: { ...Object.fromEntries(Object.entries(useCase.parameters ?? {}).map(([k, v]) => [k, String(v)])), ...request.params },
        useCase: useCase.key,
      }}
    />
    </Suspense>
  );
}
